import { useNavigate } from "react-router-dom";
import {
  Users,
  Flame,
  CalendarClock,
  Trophy,
  TrendingUp,
  Activity,
  PhoneCall,
  AlertCircle,
} from "lucide-react";
import { BUCKET_META } from "@/lib/crmHelpers";

const StatBlock = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div
    data-testid={testId}
    className="group relative overflow-hidden rounded-xl border border-border/60 bg-card p-5 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all duration-200 hover:border-border hover:shadow-[0_4px_16px_rgba(0,0,0,0.04)] dark:shadow-none"
  >
    <div
      className={`absolute left-0 top-0 h-full w-[3px] ${accent.bar}`}
      aria-hidden
    />
    <div className="flex items-start justify-between">
      <div>
        <div className="text-label text-muted-foreground">{label}</div>
        <div className="font-heading mt-3 text-3xl font-semibold tracking-tight">
          {value}
        </div>
        {sub && (
          <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
        )}
      </div>
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent.bg}`}
      >
        <Icon className={`h-4 w-4 ${accent.text}`} strokeWidth={2} />
      </div>
    </div>
  </div>
);

export default function HeaderStats({ stats, loading }) {
  const navigate = useNavigate();
  const s = stats || {
    total: 0,
    chauds: 0,
    relances_today: 0,
    gagne: 0,
    actifs: 0,
    taux_conversion: 0,
    actions_today: 0,
    buckets: { hot: 0, warm: 0, nurture: 0, won: 0, lost: 0, pending: 0 },
    circuit_24h: { total: 0, retard: 0, urgent: 0 },
    segments: { dakar: 0, diaspora: 0 },
  };

  const blocks = [
    {
      label: "Total leads",
      value: s.total,
      icon: Users,
      testId: "stat-total",
      sub: `${s.segments?.dakar || 0} Dakar · ${s.segments?.diaspora || 0} Diaspora`,
      accent: {
        bar: "bg-zinc-400 dark:bg-zinc-500",
        bg: "bg-zinc-100 dark:bg-zinc-800",
        text: "text-zinc-700 dark:text-zinc-300",
      },
    },
    {
      label: "Leads HOT",
      value: s.buckets?.hot || 0,
      icon: Flame,
      testId: "stat-hot",
      sub: `${s.buckets?.warm || 0} INTÉRESSÉ · ${s.buckets?.nurture || 0} NURTURE`,
      accent: {
        bar: "bg-red-500",
        bg: "bg-red-50 dark:bg-red-950/40",
        text: "text-red-600 dark:text-red-400",
      },
    },
    {
      label: "Circuit 24h",
      value: s.circuit_24h?.total || 0,
      icon: PhoneCall,
      testId: "stat-circuit",
      sub:
        s.circuit_24h?.retard > 0
          ? `⚠ ${s.circuit_24h.retard} en retard`
          : "À contacter dans 24h",
      accent: {
        bar: "bg-[hsl(var(--accent-navy))]",
        bg: "bg-blue-50 dark:bg-blue-950/40",
        text: "text-[hsl(var(--accent-navy))] dark:text-blue-300",
      },
    },
    {
      label: "Ventes conclues",
      value: s.gagne,
      icon: Trophy,
      testId: "stat-gagne",
      sub: `Taux conversion ${s.taux_conversion}%`,
      accent: {
        bar: "bg-emerald-500",
        bg: "bg-emerald-50 dark:bg-emerald-950/40",
        text: "text-emerald-600 dark:text-emerald-400",
      },
    },
  ];

  const bucketChips = [
    { key: "hot", count: s.buckets?.hot || 0 },
    { key: "warm", count: s.buckets?.warm || 0 },
    { key: "nurture", count: s.buckets?.nurture || 0 },
    { key: "pending", count: s.buckets?.pending || 0 },
    { key: "won", count: s.buckets?.won || 0 },
    { key: "lost", count: s.buckets?.lost || 0 },
  ];

  return (
    <div className="space-y-4">
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="header-stats"
      >
        {blocks.map((b) => (
          <StatBlock key={b.label} {...b} />
        ))}
      </div>

      {/* Bucket breakdown — chips clickable → /leads filtered by bucket */}
      <div
        className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-3"
        data-testid="bucket-breakdown"
      >
        <span className="text-label pr-1 text-muted-foreground">Classement</span>
        {bucketChips.map((b) => {
          const m = BUCKET_META[b.key];
          const disabled = b.count === 0;
          return (
            <button
              key={b.key}
              type="button"
              disabled={disabled}
              onClick={() => navigate(`/leads?bucket=${b.key}`)}
              data-testid={`bucket-chip-${b.key}`}
              title={disabled ? "Aucun lead dans cette catégorie" : `Voir les ${b.count} lead${b.count > 1 ? "s" : ""} ${m.label}`}
              aria-label={`Filtrer la liste sur ${m.label} (${b.count})`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-all ${m.color} ${
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:-translate-y-0.5 hover:shadow-soft hover:brightness-95 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
              {m.label}
              <span className="font-bold">{b.count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-dashed border-border/60 bg-muted/20 px-5 py-3 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <TrendingUp className="h-4 w-4" />
          <span>Taux de conversion</span>
          <span
            className="font-heading font-semibold text-foreground"
            data-testid="stat-conversion"
          >
            {s.taux_conversion}%
          </span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Activity className="h-4 w-4" />
          <span>Actions aujourd'hui</span>
          <span
            className="font-heading font-semibold text-foreground"
            data-testid="stat-actions-today"
          >
            {s.actions_today}
          </span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>Leads actifs</span>
          <span
            className="font-heading font-semibold text-foreground"
            data-testid="stat-actifs"
          >
            {s.actifs}
          </span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <CalendarClock className="h-4 w-4" />
          <span>Relances du jour</span>
          <span
            className="font-heading font-semibold text-foreground"
            data-testid="stat-relances"
          >
            {s.relances_today}
          </span>
        </div>
        {s.circuit_24h?.retard > 0 && (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            <span className="font-semibold">
              {s.circuit_24h.retard} lead(s) 24h dépassé
            </span>
          </div>
        )}
        {loading && (
          <span className="text-xs text-muted-foreground">Chargement...</span>
        )}
      </div>
    </div>
  );
}
